/**
 * Utilidades para mapear y transformar campos de JotForm
 * a estructuras más amigables para el frontend
 */

// ============================================================================
// TIPOS
// ============================================================================

export interface JotFormField {
  name: string;
  text: string;
  type: string;
  order?: string;
  answer?: any;
  [key: string]: any;
}

export interface MatrixRow {
  id: string;
  text: string;
  answer: string | number;
}

export interface MappedMatrixField {
  question_id: string;
  question_text: string;
  question_type: 'control_matrix';
  rows: MatrixRow[];
}

// ============================================================================
// FUNCIONES DE MAPEO - CONTROL MATRIX
// ============================================================================

/**
 * Parsea las filas de una matriz desde el campo drows (JSON) o mrows (string)
 */
function parseMatrixRows(field: JotFormField): Array<{ id: string; text: string }> {
  // Intentar parsear desde drows (JSON)
  if (field.drows) {
    try {
      const parsed = JSON.parse(field.drows);
      return parsed.map((row: any) => ({
        id: row.id || row.rowId || '',
        text: row.text || '',
      }));
    } catch (e) {
      console.warn('Error parseando drows:', e);
    }
  }

  // Fallback a mrows (string delimitado por |)
  if (field.mrows) {
    const rowTexts = field.mrows.split('|');
    const rowIds = field.rowIds || [];
    
    return rowTexts.map((text: string, index: number) => ({
      id: rowIds[index] || index.toString(),
      text: text.trim(),
    }));
  }

  return [];
}

/**
 * Mapea un campo de tipo control_matrix a una estructura más amigable
 */
export function mapControlMatrixField(
  questionId: string,
  field: JotFormField
): MappedMatrixField | null {
  if (field.type !== 'control_matrix') {
    console.warn(`Campo ${questionId} no es de tipo control_matrix`);
    return null;
  }

  // Parsear las filas
  const rows = parseMatrixRows(field);
  
  // Obtener las respuestas (array bidimensional)
  const answers = field.answer || [];
  
  // Mapear cada fila con su respuesta correspondiente
  const mappedRows: MatrixRow[] = rows.map((row, index) => {
    // La respuesta está en answer[i][0] según el formato de JotForm
    const answer = answers[index] && answers[index][0] 
      ? answers[index][0] 
      : '';
    
    return {
      id: row.id,
      text: row.text,
      answer: answer,
    };
  });

  return {
    question_id: questionId,
    question_text: field.text || '',
    question_type: 'control_matrix',
    rows: mappedRows,
  };
}

// ============================================================================
// FUNCIÓN PRINCIPAL DE MAPEO
// ============================================================================

/**
 * Mapea cualquier campo de JotForm según su tipo
 */
export function mapJotFormField(questionId: string, field: JotFormField): any {
  switch (field.type) {
    case 'control_matrix':
      return mapControlMatrixField(questionId, field);
    
    // Aquí se pueden agregar más tipos de campos en el futuro
    // case 'control_scale':
    //   return mapScaleField(questionId, field);
    // case 'control_radio':
    //   return mapRadioField(questionId, field);
    
    default:
      console.warn(`Tipo de campo no soportado: ${field.type}`);
      return null;
  }
}

// ============================================================================
// UTILIDADES DE RENDERIZADO
// ============================================================================

/**
 * Obtiene el color para una calificación en escala del 0-10
 */
export function getRatingColor(rating: number): string {
  if (rating >= 9) return 'text-green-600 dark:text-green-400';
  if (rating >= 7) return 'text-blue-600 dark:text-blue-400';
  if (rating >= 5) return 'text-yellow-600 dark:text-yellow-400';
  if (rating >= 3) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
}

/**
 * Obtiene el color de fondo para una calificación
 */
export function getRatingBgColor(rating: number): string {
  if (rating >= 9) return 'bg-green-100 dark:bg-green-900/30';
  if (rating >= 7) return 'bg-blue-100 dark:bg-blue-900/30';
  if (rating >= 5) return 'bg-yellow-100 dark:bg-yellow-900/30';
  if (rating >= 3) return 'bg-orange-100 dark:bg-orange-900/30';
  return 'bg-red-100 dark:bg-red-900/30';
}
